"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card } from "@/components/ui/card";
import { Phone, Mail, MapPin, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section ref={ref} className="min-h-screen bg-light-background py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: `repeating-linear-gradient(0deg, hsl(0, 0%, 0%), hsl(0, 0%, 0%) 1px, transparent 1px, transparent 40px),
                           repeating-linear-gradient(90deg, hsl(0, 0%, 0%), hsl(0, 0%, 0%) 1px, transparent 1px, transparent 40px)`
    }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 transition-all duration-[150ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          {}
          <FormProvider className="flex items-center" formId="contact-form-hero" sectionName="contact-form" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon.">
            <ContactFormContent />
          </FormProvider>

          {}
          <div className="flex flex-col justify-center space-y-6">
            <div className={`transition-all duration-[150ms] delay-100 ease-[cubic-bezier(0.4,0,0.2,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
              <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground-light mb-2">
                Get in Touch
              </h2>
              <p className="text-muted-foreground text-base">
                Let's discuss how we can help you achieve your goals.
              </p>
            </div>

            <div className="space-y-5">
              <ContactDetailItem icon={<Phone className="size-5" />} label="Phone" value="541-215-6033" delay={150} isVisible={isVisible} />
              <ContactDetailItem icon={<Mail className="size-5" />} label="Email" value="info@freedomproductionsor.com" delay={200} isVisible={isVisible} />
              <ContactDetailItem icon={<MapPin className="size-5" />} label="Address" value="789 Creative Lane, Los Angeles, CA 90001" delay={250} isVisible={isVisible} />
            </div>

            <div className={`pt-6 transition-all duration-[150ms] delay-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground-light">
                  Office Hours:
                </span>
                <br />
                Mon-Fri: 9am - 6pm
                <br />
                Sat: 10am - 2pm
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="w-full bg-muted-light-background border border-border shadow-soft p-8">
        <div className="text-center space-y-4">
          <div className="size-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
            <Mail className="size-8 text-primary" />
          </div>
          <h3 className="text-2xl font-display font-semibold text-foreground-light">
            Message Sent!
          </h3>
          <p className="text-muted-foreground">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="mt-4">
            Send Another Message
          </Button>
        </div>
      </Card>;
  }
  return <Card className="w-full bg-muted-light-background border border-border shadow-soft transition-all duration-[150ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-medium hover:-translate-y-0.5">
      <div className="p-8">
        <h3 className="text-2xl font-display font-semibold text-foreground-light mb-6">
          Send us a Message
        </h3>

        <Form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <FormField control={control} name="name" rules={{
            required: "Name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your Name" {...field} className="transition-all duration-[150ms] focus:shadow-soft" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} className="transition-all duration-[150ms] focus:shadow-soft" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[\d\s\-\+\(\)]+$/,
              message: "Invalid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone (Optional)</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(323) 555-7890" {...field} className="transition-all duration-[150ms] focus:shadow-soft" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" rules={{
            required: "Message is required",
            minLength: {
              value: 10,
              message: "Message must be at least 10 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Tell us about your project..." className="min-h-32 transition-all duration-[150ms] focus:shadow-soft" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-md">
                <p className="text-destructive text-sm">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[80ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-[1.02] active:scale-100">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Sending...
                </> : "Send Message"}
            </Button>
          </div>
        </Form>
      </div>
    </Card>;
}
function ContactDetailItem({
  icon,
  label,
  value,
  delay,
  isVisible
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  delay: number;
  isVisible: boolean;
}) {
  return <div className={`flex items-start gap-4 transition-all duration-[150ms] ease-[cubic-bezier(0.4,0,0.2,1)]`} style={{
    transitionDelay: `${delay}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)"
  }}>
      <div className="flex-shrink-0 size-12 bg-primary rounded-md flex items-center justify-center text-foreground-dark">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-muted-foreground mb-0.5">{label}</p>
        <p className="text-base text-foreground-light break-words">{value}</p>
      </div>
    </div>;
}